import { useEffect, useState } from "react";
import { IoNotificationsOutline, IoCheckmarkCircle, IoCloseCircle, IoClose } from "react-icons/io5";
import api from "../api";

export default function PushNotificationPrompt() {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("checking"); // checking, granted, denied, unsupported, prompt

  useEffect(() => {
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = async () => {
    // بررسی پشتیبانی مرورگر
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      setStatus("unsupported");
      setShow(false);
      return;
    }

    // بررسی اینکه آیا قبلاً granted یا این session نشون داده شده
    const shownThisSession = sessionStorage.getItem("pushPromptShown");
    const permission = Notification.permission;

    if (permission === "granted") {
      setStatus("granted");
      setShow(false);
      
      // ثبت service worker و ارسال subscription به سرور
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        if (subscription) {
          await api.post("/push/subscribe", {
            subscription: subscription.toJSON(),
          });
        }
      } catch (error) {
        // Silent fail - don't show error to user
        if (error.response?.status === 400) {
          console.debug("Push subscription skipped (not supported on this device)");
        } else {
          console.debug("Push subscription error (ignored):", error.response?.status);
        }
      }
      
      return;
    }

    // اگر این session نشون داده شده، دیگه نشون نده
    if (shownThisSession === "true") {
      setShow(false);
      return;
    }

    // اگر denied است، نشون نده (کاربر قبلاً رد کرده)
    if (permission === "denied") {
      setStatus("denied");
      setShow(false);
      return;
    }

    // اگر هنوز درخواست نشده و این session نشون داده نشده
    setStatus("prompt");
    setShow(true);
    
    // علامت بزن که این session نشون داده شد
    sessionStorage.setItem("pushPromptShown", "true");
  };

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        setStatus("granted");
        setShow(false);

        // ثبت service worker
        const registration = await navigator.serviceWorker.ready;
        
        // دریافت VAPID public key از سرور
        const { data } = await api.get("/push/vapid-public-key");
        
        // Subscribe کردن
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });

        // ارسال subscription به سرور
        await api.post("/push/subscribe", {
          subscription: subscription.toJSON(),
        });

      } else {
        // کاربر رد کرد، modal رو ببند
        setShow(false);
      }
    } catch (error) {
      console.debug("خطا در درخواست اجازه (ignored):", error);
      setShow(false);
    }
  };

  const handleClose = () => {
    // کاربر دکمه بستن رو زد، modal رو ببند
    setShow(false);
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-[slideUp_0.3s_ease-out] relative">
        {/* دکمه بستن */}
        <button
          onClick={handleClose}
          className="absolute top-4 left-4 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          aria-label="بستن"
        >
          <IoClose className="text-2xl" />
        </button>

        {status === "prompt" && (
          <>
            <div className="flex justify-center mb-4">
              <div className="bg-blue-100 p-4 rounded-full">
                <IoNotificationsOutline className="text-blue-600 text-5xl" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-800 mb-3">
              فعال‌سازی اعلان‌ها
            </h2>

            <p className="text-gray-600 text-center mb-6 leading-relaxed">
              برای دریافت اطلاع‌رسانی درباره معاملات، سفارشات و تغییرات قیمت،
              توصیه می‌کنیم دسترسی به اعلان‌ها را فعال کنید.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-800 text-center">
                <IoCheckmarkCircle className="inline text-lg mb-0.5" /> این
                دسترسی کاملاً <strong>اختیاری</strong> است و می‌توانید بعداً
                آن را فعال کنید.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 rounded-xl transition-all"
              >
                بعداً
              </button>
              <button
                onClick={requestPermission}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
              >
                فعال‌سازی
              </button>
            </div>
          </>
        )}

        {status === "denied" && (
          <>
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 p-4 rounded-full">
                <IoCloseCircle className="text-red-600 text-5xl" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-800 mb-3">
              دسترسی به اعلان‌ها رد شد
            </h2>

            <p className="text-gray-600 text-center mb-4 leading-relaxed">
              برای دریافت اطلاع‌رسانی‌ها، می‌توانید دسترسی به اعلان‌ها را از
              تنظیمات مرورگر فعال کنید.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-700 font-bold mb-2">
                راهنمای فعال‌سازی:
              </p>
              <ol className="text-sm text-gray-600 space-y-2 mr-4 list-decimal">
                <li>روی آیکون قفل کنار آدرس سایت کلیک کنید</li>
                <li>گزینه "اعلان‌ها" یا "Notifications" را پیدا کنید</li>
                <li>وضعیت آن را به "مجاز" یا "Allow" تغییر دهید</li>
                <li>صفحه را رفرش کنید</li>
              </ol>
            </div>

            <button
              onClick={handleClose}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 rounded-xl transition-all"
            >
              فهمیدم
            </button>
          </>
        )}

        {status === "unsupported" && (
          <>
            <div className="flex justify-center mb-4">
              <div className="bg-gray-100 p-4 rounded-full">
                <IoCloseCircle className="text-gray-600 text-5xl" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-800 mb-3">
              مرورگر پشتیبانی نمی‌کند
            </h2>

            <p className="text-gray-600 text-center mb-4 leading-relaxed">
              متأسفانه مرورگر شما از اعلان‌های وب پشتیبانی نمی‌کند.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-700 font-bold mb-2">
                مرورگرهای پشتیبانی‌شده:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 mr-4 list-disc">
                <li>Chrome 42+</li>
                <li>Firefox 44+</li>
                <li>Edge 17+</li>
                <li>Safari 16.4+ (iOS 16.4+)</li>
                <li>Opera 37+</li>
              </ul>
            </div>

            <button
              onClick={handleClose}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 rounded-xl transition-all"
            >
              فهمیدم
            </button>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
